import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import styles from './BudgetLinesTable.module.scss';
import utilsStyles from '../Utils.module.scss';
import { useTranslation } from 'react-i18next';
import BudgetLinesList from '../linesList/BudgetLinesList';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LinesDataProvider } from '../LinesDataProvider';
import useSticky from 'common/hooks/useSticky';
// Auto-scroll desativado — import só usado pelo scrollToLine (também comentado).
// import { BUDGET_LINE_ID_PREFIX } from '../line/BudgetLine';
import { BODY_CLICK_IGNORE_CLASS_NAME, getOrderedLineColumns, LinesTableColumn } from '../utils';
import BudgetLineColumn from '../column/BudgetLineColumn';
import { useSelector, useDispatch } from 'react-redux';
import { Reducers } from 'store/types';
import { setSelectedLineKey, updateAllLines } from 'store/budget/action';
import InformationModal from 'common/components/modal/informationModal/InformationModal';
import InfoIcon from 'common/components/infoIcon/InfoIcon';
import store from 'store/store';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import MathUtils from 'common/services/MathUtils';
import { BODY_TOOLTIP, DEFAULT_DECIMAL_PLACES } from 'Config';
import Button from 'common/components/button/Button';
import { FaAngleDown, FaAngleRight } from 'react-icons/fa';
import Loading from 'common/services/Loading';

interface ColumnConfig {
    label: string;
    tooltipTitle?: string;
    tooltipMessage?: string;
    columnClassName: string;
}

interface Props {
    disabled: boolean;
}

function BudgetLinesTable({ disabled }: Props): JSX.Element | null {
    const { t } = useTranslation();
    // Auto-scroll desativado — selector só era usado pelo useEffect de scroll (também comentado).
    // const selectedLineKey = useSelector<Reducers, string | null>(x => x.budget.selectedLineKey);
    const dispatch = useDispatch();
    const { ref: headerRef, stickyData } = useSticky<HTMLDivElement>();
    const containerRef = useRef<HTMLDivElement>(null);
    const lines = useSelector<Reducers, BudgetLineDto[]>(x => x.budget.lines);
    const decimalPlaces = useSelector<Reducers, number>(x => x.budget.budget.decimalPlaces ?? DEFAULT_DECIMAL_PLACES);
    const { isComponentEditor, columnsOrder } = useContext(LinesDataProvider);
    const showColumnTotals = !isComponentEditor;
    const [currentInfoTooltip, setCurrentInfoTooltip] = useState<{ title: string; message: string } | null>(null);

    const orderedColumns = useMemo(() => getOrderedLineColumns(columnsOrder), [columnsOrder]);

    const isAnyOpen = useMemo(
        () => lines.some(l =>
            (l.lineType === BudgetLineType.SECTION && l.isOpen) ||
            ((l.lineType === BudgetLineType.ELEMENT
                || l.lineType === BudgetLineType.COMPONENT_ELEMENT
                || l.lineType === BudgetLineType.COMPONENT) && l.tabsOpen)
        ),
        [lines]
    );

    const onToggleAll = () => {
        if (isAnyOpen) {
            dispatch(updateAllLines({ isOpen: false, tabsOpen: false }));
            return;
        }
        Loading.show();
        // Defer the heavy dispatch so the browser paints the loading overlay first;
        // expanding re-renders every line and can block the main thread.
        setTimeout(() => {
            dispatch(updateAllLines({ isOpen: true }));
            setTimeout(() => Loading.hide(), 0);
        }, 50);
    };

    const columnConfigs: Record<LinesTableColumn, ColumnConfig | undefined> = useMemo(() => ({
        [LinesTableColumn.DESIGNATION]: { label: t('budgets.budget.lines.header.designation'), columnClassName: utilsStyles.designationColumn },
        [LinesTableColumn.QUANTITY]: { label: t('budgets.budget.lines.header.quantity'), tooltipTitle: t('budgets.budget.lines.header.quantity'), tooltipMessage: t('budgets.budget.lines.header.quantity_tooltip'), columnClassName: utilsStyles.quantityColumn },
        [LinesTableColumn.UNIT]: { label: t('budgets.budget.lines.header.unity'), tooltipTitle: t('budgets.budget.lines.header.unity'), tooltipMessage: t('budgets.budget.lines.header.unity_tooltip'), columnClassName: utilsStyles.unityColumn },
        [LinesTableColumn.GROSS_UNIT_PRICE]: { label: t('budgets.budget.lines.header.gross_unit_price_short'), tooltipTitle: t('budgets.budget.lines.header.gross_unit_price'), tooltipMessage: t('budgets.budget.lines.header.gross_unit_price_tooltip'), columnClassName: utilsStyles.grossUnityColumn },
        [LinesTableColumn.DISCOUNT]: { label: t('budgets.budget.lines.header.discount'), tooltipTitle: t('budgets.budget.lines.header.discount'), tooltipMessage: t('budgets.budget.lines.header.discount_tooltip'), columnClassName: utilsStyles.discountColumn },
        [LinesTableColumn.AGGREGATED_ELEMENTS]: { label: t('budgets.budget.lines.header.aggregated_elements_short'), tooltipTitle: t('budgets.budget.lines.header.aggregated_elements'), tooltipMessage: t('budgets.budget.lines.header.aggregated_elements_tooltip'), columnClassName: utilsStyles.aggregatedElementsColumn },
        [LinesTableColumn.TOTAL_COST_UNIT_PRICE]: { label: t('budgets.budget.lines.header.total_cost_unit_price_short'), tooltipTitle: t('budgets.budget.lines.header.total_cost_unit_price'), tooltipMessage: t('budgets.budget.lines.header.total_cost_unit_price_tooltip'), columnClassName: utilsStyles.totalCostUnitPriceColumn },
        [LinesTableColumn.MARGIN]: { label: t('budgets.budget.lines.header.margin_short'), tooltipTitle: t('budgets.budget.lines.header.margin'), tooltipMessage: t('budgets.budget.lines.header.margin_tooltip'), columnClassName: utilsStyles.marginColumn },
        [LinesTableColumn.DECENNIAL_INSURANCE]: { label: t('budgets.budget.lines.header.decennial_insurance_short'), columnClassName: utilsStyles.decennialInsuranceColumn },
        [LinesTableColumn.UNIT_PRICE]: { label: t('budgets.budget.lines.header.price_per_unity'), tooltipTitle: t('budgets.budget.lines.header.price_per_unity'), tooltipMessage: t('budgets.budget.lines.header.price_per_unity_tooltip'), columnClassName: utilsStyles.pricePerUnityColumn },
        [LinesTableColumn.TOTAL_EXCLUDING_TAX]: { label: t('budgets.budget.lines.header.total_without_tax'), tooltipTitle: t('budgets.budget.lines.header.total_without_tax'), tooltipMessage: t('budgets.budget.lines.header.total_without_tax_tooltip'), columnClassName: utilsStyles.totalWithoutTaxColumn },
        [LinesTableColumn.TAX]: { label: t('budgets.budget.lines.header.tax'), tooltipTitle: t('budgets.budget.lines.header.tax'), tooltipMessage: t('budgets.budget.lines.header.tax_tooltip'), columnClassName: utilsStyles.taxColumn },
        [LinesTableColumn.TOTAL_INCLUDING_TAX]: { label: t('budgets.budget.lines.header.total_with_tax'), tooltipTitle: t('budgets.budget.lines.header.total_with_tax'), tooltipMessage: t('budgets.budget.lines.header.total_with_tax_tooltip'), columnClassName: utilsStyles.totalWithTaxColumn },
        [LinesTableColumn.NET_PROFIT]: { label: t('budgets.budget.lines.header.net_profit_short'), tooltipTitle: t('budgets.budget.lines.header.net_profit'), tooltipMessage: t('budgets.budget.lines.header.net_profit_tooltip'), columnClassName: utilsStyles.netProfitColumn },
        [LinesTableColumn.NET_PROFIT_PERCENTAGE]: { label: t('budgets.budget.lines.header.net_profit_percentage_short'), tooltipTitle: t('budgets.budget.lines.header.net_profit_percentage'), tooltipMessage: t('budgets.budget.lines.header.net_profit_percentage_tooltip'), columnClassName: utilsStyles.netProfitPercentageColumn },
        [LinesTableColumn.ELEMENT_IMAGE]: undefined,
        [LinesTableColumn.LINE_NUMBER]: undefined,
    }), [t]);

    const renderHeaderCell = (col: LinesTableColumn) => {
        const cfg = columnConfigs[col];
        if (!cfg) return null;
        return (
            <BudgetLineColumn key={col} column={col} className={`${styles.column} ${utilsStyles.column} ${cfg.columnClassName}`}>
                {cfg.label}
                {cfg.tooltipMessage && (
                    <InfoIcon onClick={() => setCurrentInfoTooltip({ title: cfg.tooltipTitle ?? cfg.label, message: cfg.tooltipMessage! })} />
                )}
            </BudgetLineColumn>
        );
    };

    const columnTotals = useMemo(() => {
        const lineByKey = new Map<string, BudgetLineDto>();
        for (const l of lines) lineByKey.set(l.key, l);

        const isPriced = (l: BudgetLineDto) =>
            l.lineType === BudgetLineType.ELEMENT ||
            l.lineType === BudgetLineType.COMPONENT_ELEMENT ||
            l.lineType === BudgetLineType.COMPONENT;

        // True if any ancestor is a priced line — means this line's value is already rolled up
        // into that ancestor's totalExcludingTax (composition products or treatments).
        const hasPricedAncestor = (line: BudgetLineDto): boolean => {
            let parentKey = line.parentKey;
            while (parentKey) {
                const parent = lineByKey.get(parentKey);
                if (!parent) break;
                if (isPriced(parent)) return true;
                parentKey = parent.parentKey;
            }
            return false;
        };

        const hasCustomTotalText = (line: BudgetLineDto) => typeof line.customTotalText === 'string' && line.customTotalText.length > 0;

        // True if any ancestor has a custom total text — that ancestor is "muted" from rollups,
        // so its descendants must not contribute to the column totals either.
        const hasMutedAncestor = (line: BudgetLineDto): boolean => {
            let parentKey = line.parentKey;
            while (parentKey) {
                const parent = lineByKey.get(parentKey);
                if (!parent) break;
                if (hasCustomTotalText(parent)) return true;
                parentKey = parent.parentKey;
            }
            return false;
        };

        const lineKeys = new Set(lines.map(l => l.key));
        let totalExcludingTax = 0;
        let totalIncludingTax = 0;
        let netProfit = 0;

        // Totais s/IVA e c/IVA: somar as linhas de topo (cada uma já inclui o rollup da sua
        // subárvore), tal como o total do orçamento e as linhas de secção. Somar as folhas com
        // preço duplicava os elementos cuja subárvore já está contabilizada na linha-pai quando
        // existe um nível intermédio que não entra no rollup da secção.
        for (const line of lines) {
            const isRoot = line.parentKey === null || !lineKeys.has(line.parentKey);
            if (!isRoot) continue;
            if (hasCustomTotalText(line)) continue;
            totalExcludingTax = MathUtils.plus(totalExcludingTax, line.totalExcludingTax);
            totalIncludingTax = MathUtils.plus(totalIncludingTax, line.totalIncludingTax);
        }

        // Lucro líquido: métrica por elemento — soma o lucro de cada linha com preço de topo
        // (cada uma já reflete o custo dos seus agregados via unitPriceWithChildren).
        for (const line of lines) {
            if (!isPriced(line)) continue;
            if (hasPricedAncestor(line)) continue;
            // The line itself or any ancestor with a custom total text → don't contribute.
            if (hasCustomTotalText(line)) continue;
            if (hasMutedAncestor(line)) continue;

            const qty = line.quantity ?? 0;
            const elementCostUnitPrice = MathUtils.plus(
                line.grossUnitPrice,
                MathUtils.multiply(line.grossUnitPrice, MathUtils.divide(line.discountPercentage, 100)) * -1,
                line.unitPriceWithChildren
            );

            netProfit = MathUtils.plus(
                netProfit,
                MathUtils.multiply(MathUtils.minus(line.unitPrice, elementCostUnitPrice), qty)
            );
        }

        return { totalExcludingTax, totalIncludingTax, netProfit };
    }, [lines]);

    const renderTotalsCell = (col: LinesTableColumn) => {
        const cfg = columnConfigs[col];
        if (!cfg) return null;
        let content: JSX.Element | null = null;
        if (col === LinesTableColumn.TOTAL_EXCLUDING_TAX) {
            content = <MoneyFormat value={columnTotals.totalExcludingTax} decimalScale={decimalPlaces} />;
        } else if (col === LinesTableColumn.TOTAL_INCLUDING_TAX) {
            content = <MoneyFormat value={columnTotals.totalIncludingTax} decimalScale={decimalPlaces} />;
        } else if (col === LinesTableColumn.NET_PROFIT) {
            content = <MoneyFormat value={columnTotals.netProfit} decimalScale={decimalPlaces} />;
        }
        return (
            <BudgetLineColumn key={col} column={col} className={`${styles.totalsColumn} ${utilsStyles.column} ${cfg.columnClassName}`}>
                {content}
            </BudgetLineColumn>
        );
    };

    // Auto-scroll desativado — refs auxiliares só usados pelo scrollToLine (também comentado).
    // const previousSelectedLineKeyRef = useRef<string | null>(null);
    // const scrollTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

    const onClickBody = (ev: MouseEvent) => {
        if (ev && ev.target) {
            const target = ev.target as HTMLElement;
            const targetId = target.id;

            if (
                targetId.includes('react-select-') ||
                (target && target.className && typeof target.className === 'string' && target.className.includes(BODY_CLICK_IGNORE_CLASS_NAME))
            ) {
                return;
            }

            // Check if click is inside a react-select portal menu
            if (target.closest?.('[class*="menu"]')?.querySelector?.('[id*="react-select-"]') ||
                target.closest?.('[id*="react-select-"]')) {
                return;
            }
        }

        if (store.getState().budget.selectedLineKeys.length > 0) {
            dispatch(setSelectedLineKey(null));
        }
    }

    // Auto-scroll desativado — função só usada pelo useEffect de scroll (também comentado).
    // const scrollToLine = (lineKey: string, retries = 0) => {
    //     const lineElement = document.getElementById(BUDGET_LINE_ID_PREFIX + lineKey);
    //     if (!lineElement) {
    //         if (retries < 10) {
    //             const t = setTimeout(() => {
    //                 scrollTimeoutsRef.current.delete(t);
    //                 scrollToLine(lineKey, retries + 1);
    //             }, 100);
    //             scrollTimeoutsRef.current.add(t);
    //         }
    //         return;
    //     }
    //
    //     const scrollToOptions = {
    //         block: 'start',
    //         behavior: 'smooth'
    //     };
    //     lineElement.scrollIntoView(scrollToOptions as unknown as ScrollToOptions);
    // }

    useEffect(() => {
        document.body.addEventListener('click', onClickBody);

        return function cleanup() {
            document.body.removeEventListener('click', onClickBody);
            // Auto-scroll desativado — cleanup dos timeouts deixou de ser necessário.
            // scrollTimeoutsRef.current.forEach(clearTimeout);
            // scrollTimeoutsRef.current.clear();
        }
    }, []);

    // Auto-scroll desativado: ao selecionar uma linha já não se faz scroll automático.
    // useEffect(() => {
    //     if (selectedLineKey) {
    //         scrollToLine(selectedLineKey);
    //         previousSelectedLineKeyRef.current = selectedLineKey;
    //     } else if (previousSelectedLineKeyRef.current) {
    //         const previousKey = previousSelectedLineKeyRef.current;
    //         // Small delay to let the DOM collapse before scrolling
    //         const t = setTimeout(() => {
    //             scrollTimeoutsRef.current.delete(t);
    //             scrollToLine(previousKey);
    //         }, 50);
    //         scrollTimeoutsRef.current.add(t);
    //     }
    // }, [selectedLineKey])

    return (
        <div className={styles.container} ref={containerRef}>
            <div className={styles.table}>
                <div className={`${styles.header} ${stickyData.topIsSticky ? styles.headerSticky : ''}`} ref={headerRef}>
                    <Button
                        variant='empty'
                        size='small'
                        className={styles.toggleAllButton}
                        onClick={onToggleAll}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={isAnyOpen ? t('budgets.budget.lines.collapse_all_lines') : t('budgets.budget.lines.expand_all_lines')}
                    >
                        {isAnyOpen ? <FaAngleDown /> : <FaAngleRight />}
                    </Button>
                    {renderHeaderCell(LinesTableColumn.DESIGNATION)}
                    {orderedColumns.map(col => renderHeaderCell(col))}
                </div>
                {showColumnTotals && (
                    <div className={`${styles.totalsRow} ${stickyData.topIsSticky ? styles.totalsRowSticky : ''}`}>
                        <div className={styles.toggleAllSpacer} />
                        {renderTotalsCell(LinesTableColumn.DESIGNATION)}
                        {orderedColumns.map(col => renderTotalsCell(col))}
                    </div>
                )}
                <div className={styles.body}>
                    <BudgetLinesList
                        lines={lines}
                        disabled={disabled}
                    />
                </div>
            </div>

            <InformationModal
                title={currentInfoTooltip?.title}
                message={currentInfoTooltip?.message || ''}
                onClose={() => setCurrentInfoTooltip(null)}
                isOpen={Boolean(currentInfoTooltip)}
            />
        </div>
    );
}

export default BudgetLinesTable;
